import { PayloadAction, createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { TachoColumnState } from '../../core/domainModels/displayPreferences/tachoColumnState';
import { PersistConfig } from 'redux-persist';
import { fieldName } from '../../core/helpers/aggrid';
import autoMergeLevel1 from 'redux-persist/lib/stateReconciler/autoMergeLevel1';
import storage from 'redux-persist/lib/storage';
import { LicenseTerm } from '../../core/domainModels/licenseTerms/LicenseTerm';
import {
    createLicenseTerm,
    getLicenseTerms,
    removeLicenseTerm,
    updateLicenseTerm,
} from '../../core/api/endpoints/licenseTerms/licenseTermsApi';
import { AddLicenseTerm } from '../../core/domainModels/licenseTerms/AddLicenseTerm';
import { UpdateLicenseTerm } from '../../core/domainModels/licenseTerms/UpdateLicenseTerm';

export interface LicenseTermsState {
    licenseTerms: Array<LicenseTerm>;
    fetchLicenseTermsPending: boolean;
    fetchLicenseTermsError: string;
    deleteLicenseTermPending: boolean;
    deleteLicenseTermError: string;
    addLicenseTermPending: boolean;
    addLicenseTermError: string;
    updateLicenseTermPending: boolean;
    updateLicenseTermError: string;
    displayPreferences: Array<TachoColumnState>;
    tableFilters: any;

    uploadPercent: number;
    uploadLoaded: number;
    uploadTotal: number;
}

const initialState: LicenseTermsState = {
    licenseTerms: [],
    fetchLicenseTermsPending: false,
    fetchLicenseTermsError: '',
    deleteLicenseTermPending: false,
    deleteLicenseTermError: '',
    addLicenseTermPending: false,
    addLicenseTermError: '',
    updateLicenseTermPending: false,
    updateLicenseTermError: '',
    displayPreferences: [],
    tableFilters: {},
    uploadPercent: 0,
    uploadLoaded: 0,
    uploadTotal: 0,
};

const name = 'licenseTerms';

const fetchLicenseTermsThunk = createAsyncThunk(`${name}/fetch`, async (params, thunkAPI) => {
    try {
        return await getLicenseTerms();
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const deleteLicenseTermThunk = createAsyncThunk(`${name}/delete`, async (clientId: string, thunkAPI) => {
    try {
        return await removeLicenseTerm(clientId);
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const addLicenseTermThunk = createAsyncThunk(`${name}/add`, async (term: AddLicenseTerm, thunkAPI) => {
    try {
        return await createLicenseTerm(term, (percent, loaded, total) => {
            thunkAPI.dispatch(uploadFileAction({ percent, loaded, total }));
        });
    } catch (error: any) {
        return thunkAPI.rejectWithValue(error.message);
    }
});

const updateLicenseTermThunk = createAsyncThunk(`${name}/update`, async (terms: UpdateLicenseTerm, thunkAPI) => {
    try {
        return await updateLicenseTerm(terms, (percent, loaded, total) => {
            thunkAPI.dispatch(uploadFileAction({ percent, loaded, total }));
        });
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const uploadFileAction = createAction<{ percent: number; loaded: number; total: number }>('root/reset');

const distributorsSlice = createSlice({
    name,
    initialState,
    reducers: {
        modifyLicenseTermsDisplayPreferences(state, action: PayloadAction<any>) {
            state.displayPreferences = action.payload;
        },
        modifyLicenseTermsTableFilters(state, action: PayloadAction<any>) {
            state.tableFilters = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchLicenseTermsThunk.pending, (state, action) => {
                state.fetchLicenseTermsPending = true;
                state.fetchLicenseTermsError = '';
            })
            .addCase(fetchLicenseTermsThunk.fulfilled, (state, action) => {
                state.fetchLicenseTermsError = '';
                state.licenseTerms = action.payload;
                state.fetchLicenseTermsPending = false;
            })
            .addCase(fetchLicenseTermsThunk.rejected, (state, action) => {
                state.fetchLicenseTermsPending = false;
                state.fetchLicenseTermsError = action.payload as string;
            })
            .addCase(deleteLicenseTermThunk.pending, (state, action) => {
                state.deleteLicenseTermPending = true;
                state.deleteLicenseTermError = '';
            })
            .addCase(deleteLicenseTermThunk.fulfilled, (state, action) => {
                state.deleteLicenseTermError = '';
                state.licenseTerms = state.licenseTerms.filter(item => item.id !== action.payload);
                state.deleteLicenseTermPending = false;
            })
            .addCase(deleteLicenseTermThunk.rejected, (state, action) => {
                state.deleteLicenseTermPending = false;
                state.deleteLicenseTermError = action.payload as string;
            })
            .addCase(addLicenseTermThunk.pending, (state, action) => {
                state.addLicenseTermPending = true;
                state.addLicenseTermError = '';
            })
            .addCase(addLicenseTermThunk.fulfilled, (state, action) => {
                state.deleteLicenseTermError = '';
                state.addLicenseTermPending = false;
            })
            .addCase(addLicenseTermThunk.rejected, (state, action) => {
                state.addLicenseTermPending = false;
                state.addLicenseTermError = action.payload as string;
            })
            .addCase(updateLicenseTermThunk.pending, (state, action) => {
                state.updateLicenseTermPending = true;
                state.updateLicenseTermError = '';
            })
            .addCase(updateLicenseTermThunk.fulfilled, (state, action) => {
                const updatedUserIndex = state.licenseTerms.findIndex(c => c.id === action.payload.licenseTermId);
                if (updatedUserIndex !== -1) {
                    state.licenseTerms[updatedUserIndex] = { ...state.licenseTerms[updatedUserIndex], ...action.payload };
                }
                state.updateLicenseTermPending = false;
            })
            .addCase(updateLicenseTermThunk.rejected, (state, action) => {
                state.updateLicenseTermPending = false;
                state.updateLicenseTermError = action.payload as string;
            })
            .addCase(uploadFileAction, (state, action) => {
                state.uploadPercent = action.payload.percent;
                state.uploadTotal = action.payload.total;
                state.uploadLoaded = action.payload.loaded;
            });
    },
});
export const distributorsReducerConfig: PersistConfig<LicenseTermsState> = {
    key: name,
    storage: storage,
    stateReconciler: autoMergeLevel1,
    whitelist: [fieldName<LicenseTermsState>('displayPreferences'), fieldName<LicenseTermsState>('tableFilters')],
};
const { reducer, actions } = distributorsSlice;
const { modifyLicenseTermsDisplayPreferences, modifyLicenseTermsTableFilters } = actions;
export { reducer as licenseTermsReducer };
export {
    fetchLicenseTermsThunk,
    deleteLicenseTermThunk,
    addLicenseTermThunk,
    updateLicenseTermThunk,
    modifyLicenseTermsDisplayPreferences,
    modifyLicenseTermsTableFilters,
};
